import {all, call, fork, put, takeEvery} from 'bukku/shared/SagaService';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, post, puto, patch} from 'bukku/shared/ApiService';
import {errorSagaFn, requestSagaPullingFn, requestSagaPushingFn, successSagaFn} from "../helper";
import {t} from "@lingui/macro";
import { dummyAddonPacksInClients, getClientsWithDummyAddonPacks, getClientsWithDummySubscription } from './dummyData';
import { message } from 'antd';
import { useDummySubscriptionEng3265 } from '../../containers/AppPage/ControlPanel/Subscription/QuotaSubscription/DowngradeAlert/dummySubscription';

const successKeys = [
    actions.PARTNER_READ_SUCCESS,
    actions.PARTNER_UPDATE_PAYMENT_METHOD_SUCCESS,
    actions.PARTNER_DELETE_PAYMENT_METHOD_SUCCESS,
    actions.PARTNER_UPDATE_PARTNER_PROFILE_SUCCESS,
    actions.PARTNER_CREATE_CLIENT_SUCCESS,
    actions.PARTNER_READ_CLIENTS_SUCCESS,
    actions.PARTNER_READ_CLIENT_SUCCESS,
    actions.PARTNER_READ_BILLING_ESTIMATE_SUCCESS,
    actions.PARTNER_READ_REFERRALS_SUCCESS,
    actions.PARTNER_READ_TRANSACTIONS_SUCCESS,
    actions.PARTNER_READ_EVENTS_SUCCESS,
    actions.PARTNER_CHANGE_SUBSCRIPTION_SUCCESS,
    actions.PARTNER_CANCEL_SUBSCRIPTION_SUCCESS,
    actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_SUCCESS,
    actions.PARTNER_REQUEST_TO_TRANSFER_SUCCESS,
    actions.PARTNER_DELETE_CLIENT_SUCCESS,
    actions.PARTNER_READ_ADVISORS_SUCCESS,
    actions.PARTNER_CREATE_ADVISOR_SUCCESS,
    actions.PARTNER_READ_ADVISOR_SUCCESS,
    actions.PARTNER_UPDATE_ADVISOR_SUCCESS,
    actions.PARTNER_DELETE_ADVISOR_SUCCESS,
];

const errorKeys = [
    // actions.PARTNER_READ_ERROR,
    actions.PARTNER_UPDATE_PAYMENT_METHOD_ERROR,
    actions.PARTNER_DELETE_PAYMENT_METHOD_ERROR,
    actions.PARTNER_UPDATE_PARTNER_PROFILE_ERROR,
    actions.PARTNER_CREATE_CLIENT_ERROR,
    actions.PARTNER_READ_CLIENTS_ERROR,
    actions.PARTNER_READ_CLIENT_ERROR,
    actions.PARTNER_READ_BILLING_ESTIMATE_ERROR,
    actions.PARTNER_READ_REFERRALS_ERROR,
    actions.PARTNER_READ_TRANSACTIONS_ERROR,
    actions.PARTNER_READ_EVENTS_ERROR,
    actions.PARTNER_CHANGE_SUBSCRIPTION_ERROR,
    actions.PARTNER_CANCEL_SUBSCRIPTION_ERROR,
    actions.PARTNER_REQUEST_TO_TRANSFER_ERROR,
    actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_ERROR,
    actions.PARTNER_DELETE_CLIENT_ERROR,
    actions.PARTNER_READ_ADVISORS_ERROR,
    actions.PARTNER_CREATE_ADVISOR_ERROR,
    actions.PARTNER_READ_ADVISOR_ERROR,
    actions.PARTNER_UPDATE_ADVISOR_ERROR,
    actions.PARTNER_DELETE_ADVISOR_ERROR,
];

const getSuccessMsg = action => {
    switch (action.type) {
        case actions.PARTNER_CREATE_ADVISOR_SUCCESS:
            return t`The advisor is created.`;
        case actions.PARTNER_UPDATE_ADVISOR_SUCCESS:
            return t`The advisor is updated.`;
        case actions.PARTNER_DELETE_ADVISOR_SUCCESS:
            return t`The advisor is deleted.`;
        case actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_SUCCESS:
            return t`The client's subscription has been activated.`;
        case actions.PARTNER_CANCEL_SUBSCRIPTION_SUCCESS:
            return t`The client's subscription has been cancelled.`;
        case actions.PARTNER_CHANGE_SUBSCRIPTION_SUCCESS:
            return t`The client's subscription plan has been updated.`;
        case actions.PARTNER_REQUEST_TO_TRANSFER_SUCCESS:
            return t`The request has been made successfully.`;
        case actions.PARTNER_CREATE_CLIENT_SUCCESS:
            return t`New client has been created.`;
        case actions.PARTNER_DELETE_PAYMENT_METHOD_SUCCESS:
            return t`Your payment method has been removed.`;
        case actions.PARTNER_UPDATE_PAYMENT_METHOD_SUCCESS:
            return t`Your payment method has been updated.`;
        case actions.PARTNER_UPDATE_PARTNER_PROFILE_SUCCESS:
            return t`Your referral rate has been updated.`;
        default:
            return null;
    }
};

// Read partner. [PUBLIC]
export function* readRequest() {
    yield takeEvery(actions.PARTNER_READ_REQUEST, function* ({ subdomain }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/public/partners/${subdomain}`, null, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_SUCCESS,
                partner: data.partner,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Update payment method
export function* updatePaymentMethodRequest() {
    yield takeEvery(actions.PARTNER_UPDATE_PAYMENT_METHOD_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(puto, '/partner/payment_method', request, context);

            // OK
            yield put({
                type: actions.PARTNER_UPDATE_PAYMENT_METHOD_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_UPDATE_PAYMENT_METHOD_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Delete payment method
export function* deletePaymentMethodRequest() {
    yield takeEvery(actions.PARTNER_DELETE_PAYMENT_METHOD_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(deleto, '/partners/current/payment_method', request, context);

            // OK
            yield put({
                type: actions.PARTNER_DELETE_PAYMENT_METHOD_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_DELETE_PAYMENT_METHOD_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Update Referral rate
export function* updatePartnerProfileRequest() {
    yield takeEvery(actions.PARTNER_UPDATE_PARTNER_PROFILE_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(patch, '/partners/current', request, context);

            // OK
            yield put({
                type: actions.PARTNER_UPDATE_PARTNER_PROFILE_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_UPDATE_PARTNER_PROFILE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Create client
export function* createClientRequest() {
    yield takeEvery(actions.PARTNER_CREATE_CLIENT_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, '/partners/current/clients', request, context);

            // OK
            yield put({
                type: actions.PARTNER_CREATE_CLIENT_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_CREATE_CLIENT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Read clients.
export function* readClientsRequest() {
    yield takeEvery(actions.PARTNER_READ_CLIENTS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/partners/current/clients', request, context);
            const data = yield response.data;
            const {clients} = data;

            // process dummy data
            let dummyClients = clients;
            if (dummyAddonPacksInClients)
                dummyClients = getClientsWithDummyAddonPacks(clients);

            if (useDummySubscriptionEng3265)
                dummyClients = getClientsWithDummySubscription(dummyClients);
                // console.log('aaa useDummySubscriptionEng3265', dummyClients);

            const hasDummy = dummyAddonPacksInClients || useDummySubscriptionEng3265;

            yield put({
                type: actions.PARTNER_READ_CLIENTS_SUCCESS,
                // clients: data.clients,
                clients: hasDummy ? dummyClients : clients,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_CLIENTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read client.
export function* readClientRequest() {
    yield takeEvery(actions.PARTNER_READ_CLIENT_REQUEST, function* ({ clientId }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/partners/current/clients/${clientId}`, null, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_CLIENT_SUCCESS,
                client: data.client,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_CLIENT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read billing estimate.
export function* readBillingEstimateRequest() {
    yield takeEvery(actions.PARTNER_READ_BILLING_ESTIMATE_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/partners/current/billing_estimate', null, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_BILLING_ESTIMATE_SUCCESS,
                billingEstimate: data.partner_billing_estimate,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_BILLING_ESTIMATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read referrals.
export function* readReferralsRequest() {
    yield takeEvery(actions.PARTNER_READ_REFERRALS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/partners/current/referrals', request, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_REFERRALS_SUCCESS,
                referrals: data.referrals,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_REFERRALS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read transactions.
export function* readTransactionsRequest() {
    yield takeEvery(actions.PARTNER_READ_TRANSACTIONS_REQUEST, function* (payload, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/partners/current/transactions', null, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_TRANSACTIONS_SUCCESS,
                transactions: data.transactions,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_TRANSACTIONS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read Logs.
export function* readEventsRequest() {
    yield takeEvery(actions.PARTNER_READ_EVENTS_REQUEST, function* ({payload}, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/partners/current/events', payload, context);
            const data = yield response.data;

            yield put({
                type: actions.PARTNER_READ_EVENTS_SUCCESS,
                events: data.events,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_EVENTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Change subscription
export function* changeSubscriptionRequest() {
    yield takeEvery(actions.PARTNER_CHANGE_SUBSCRIPTION_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, `/partners/current/clients/${id}/change`, request, context);

            // OK
            yield put({
                type: actions.PARTNER_CHANGE_SUBSCRIPTION_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_CHANGE_SUBSCRIPTION_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Cancel subscription
export function* cancelSubscriptionRequest() {
    yield takeEvery(actions.PARTNER_CANCEL_SUBSCRIPTION_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, `/partners/current/clients/${id}/cancel`, null, context);

            // OK
            yield put({
                type: actions.PARTNER_CANCEL_SUBSCRIPTION_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_CANCEL_SUBSCRIPTION_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Subscribe subscription
export function* subscribeSubscriptionRequest() {
    yield takeEvery(actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, `/partners/current/clients/${id}/subscribe`, request, context);

            // OK
            yield put({
                type: actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_SUBSCRIBE_SUBSCRIPTION_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Request to move client to referral
export function* requestToTransferRequest() {
    yield takeEvery(actions.PARTNER_REQUEST_TO_TRANSFER_REQUEST, function* ({ id }) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(post, `/partners/current/clients/${id}/transfer`);

            // OK
            yield put({
                type: actions.PARTNER_REQUEST_TO_TRANSFER_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_REQUEST_TO_TRANSFER_ERROR,
                error,
            });
            message.error(error.message);
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Subscribe subscription
export function* deleteClientRequest() {
    yield takeEvery(actions.PARTNER_DELETE_CLIENT_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            yield call(deleto, `/partners/current/clients/${id}`, null, context);

            // OK
            yield put({
                type: actions.PARTNER_DELETE_CLIENT_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_DELETE_CLIENT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* readAdvisorListRequest() {
    yield takeEvery(actions.PARTNER_READ_ADVISORS_REQUEST, function* (action, context) {
        yield requestSagaPullingFn(action,
            function* ({request}) {
                const response = yield call(get, '/partners/current/advisors', request, context);
                const data = yield response.data;

                // OK
                yield put({
                    type: actions.PARTNER_READ_ADVISORS_SUCCESS,
                    advisors: data.advisors,
                    pagination: data.paging,
                });
            });
    });
}

export function* createAdvisorRequest() {
    yield takeEvery(actions.PARTNER_CREATE_ADVISOR_REQUEST, function* (action, context) {
        yield requestSagaPushingFn(action,
            function* ({ request }) {
                const response = yield call(post, '/partners/current/advisors', request, context);
                const data = yield response.data;

                // OK
                yield put({
                    type: actions.PARTNER_CREATE_ADVISOR_SUCCESS,
                    advisor: data.advisor,
                });
            });
    });
}

export function* readAdvisorRequest() {
    yield takeEvery(actions.PARTNER_READ_ADVISOR_REQUEST, function* (action, context) {
        yield requestSagaPullingFn(action,
            function* ({ id }) {
                const response = yield call(get, `/partners/current/advisors/${id}`, null, context);
                const data = yield response.data;

                // OK
                yield put({
                    type: actions.PARTNER_READ_ADVISOR_SUCCESS,
                    advisor: data.advisor,
                });
            });
    });
}
export function* updateAdvisorRequest() {
    yield takeEvery(actions.PARTNER_UPDATE_ADVISOR_REQUEST, function* (action, context) {
        yield requestSagaPushingFn(action,
            function* ({ id, request }) {
                const response = yield call(puto, `/partners/current/advisors/${id}`, request, context);
                const data = yield response.data;

                // OK
                yield put({
                    type: actions.PARTNER_UPDATE_ADVISOR_SUCCESS,
                    advisor: data.advisor,
                });
            });
    });
}
export function* deleteAdvisorRequest() {
    yield takeEvery(actions.PARTNER_DELETE_ADVISOR_REQUEST, function* (action, context) {
        yield requestSagaPushingFn(action,
            function* ({id, request}) {
                yield call(deleto, `/partners/current/advisors/${id}`, request, context);
                yield put({ type: actions.PARTNER_DELETE_ADVISOR_SUCCESS });
            });
    });
}

// Read client's users and form designs when partner switching client's plan
export function* readSwitchPlanInfoRequest() {
    yield takeEvery(actions.PARTNER_READ_SWITCH_PLAN_INFO_REQUEST, function* ({id}, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/partners/current/clients/${id}/switch_plan_info`, null, context);
            const data = yield response.data;

            console.log('readSwitchPlanInfoRequest', data);

            // OK
            yield put({
                type: actions.PARTNER_READ_SWITCH_PLAN_INFO_SUCCESS,
                accesses: data.accesses, // excluding advisors
                // accesses: [
                //     {
                //         id: 1,
                //         first_name: "User 1",
                //         email: "user1@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 2,
                //         first_name: "Marcus",
                //         email: "marcus@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 3,
                //         first_name: "User 3",
                //         email: "user3@bukku.my",
                //         is_owner: false,
                //     },
                //     {
                //         id: 4,
                //         first_name: "User 4 is owner",
                //         email: "user4@bukku.my",
                //         is_owner: true,
                //     },
                // ],
                formDesigns: data.form_designs,
                // formDesigns: [
                //     {
                //         id: 1,
                //         name: "FD1",
                //     },
                //     {
                //         id: 12,
                //         name: "FD12",
                //     },
                //     {
                //         id: 13,
                //         name: "FD13",
                //     },
                //     {
                //         id: 14,
                //         name: "FD14",
                //     },
                //     {
                //         id: 15,
                //         name: "FD15",
                //     },
                // ],
            });
        }
        catch (error) {
            yield put({
                type: actions.PARTNER_READ_SWITCH_PLAN_INFO_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}
export function* readSwitchPlanInfoSuccess() {
    yield takeEvery(actions.PARTNER_READ_SWITCH_PLAN_INFO_SUCCESS, () => {});
}
export function* readSwitchPlanInfoFailure() {
    yield takeEvery(actions.PARTNER_READ_SWITCH_PLAN_INFO_ERROR, function* ({ error }) {
        yield message.error(error.message);
    });
}

export default function* rootSaga() {
    yield all([
        fork(successSagaFn(successKeys, getSuccessMsg)),
        fork(errorSagaFn(errorKeys)),
        fork(readRequest),
        fork(updatePaymentMethodRequest),
        fork(deletePaymentMethodRequest),
        fork(updatePartnerProfileRequest),
        fork(createClientRequest),
        fork(readClientsRequest),
        fork(readClientRequest),
        fork(readBillingEstimateRequest),
        fork(readReferralsRequest),
        fork(readTransactionsRequest),
        fork(readEventsRequest),
        fork(changeSubscriptionRequest),
        fork(cancelSubscriptionRequest),
        fork(subscribeSubscriptionRequest),
        fork(requestToTransferRequest),
        fork(readSwitchPlanInfoRequest),
        fork(deleteClientRequest),
        fork(readAdvisorListRequest),
        fork(createAdvisorRequest),
        fork(readAdvisorRequest),
        fork(updateAdvisorRequest),
        fork(deleteAdvisorRequest),
    ]);
}
